import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { getFormData } from "src/app/shared/helpers/utils";
import { Investment } from "src/app/shared/interfaces/investments-response.interface";
import Swal from "sweetalert2";
import { InvestmentsHttpService } from "../../common/services/investments-http.service";
import { InvestmentsFormProvider } from "../../investments-form-provider";
import { NgbActiveModal, NgbDate, NgbDatepickerConfig } from "@ng-bootstrap/ng-bootstrap";
import { HttpService } from "src/app/services/http.service";
import * as moment from "moment";
import { INTERMEDIARY_ID } from "src/app/shared/enums/common.enum";
import { Subscription } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { SpinnerService } from "src/app/shared/services/spinner.service";
import { convertISONumberToNormal, formatSimpleNumber } from "@shared/helpers/v2";

@Component({
  selector: "app-add-edit-investments",
  templateUrl: "./add-edit-investments.component.html",
  styleUrls: ["./add-edit-investments.component.scss"]
})
export class AddEditInvestmentsComponent implements OnInit {
  investmentForm: FormGroup;
  // todo need remove when can get from backend side
  tooltipData = {
    ccy: "Currency",
    faceValue: "Face Value",
    comments: "Comments",
    months: "Months",
    type: "Type",
    investorId: "Investor",
    transactionType: "Transaction Type",
    date: "Date",
    product: "Product"
  };
  private changeSubscription: Subscription = new Subscription();
  isSubmitted = false;
  @Input() investorId: string;
  type: { id: string; name: string }[];
  investors: { id: string; name: string }[];
  allCcyData: { id: string; name: string }[];
  transactionType: { id: string; name: string }[];
  product: { id: string; name: string }[];
  investmentData: Investment;
  allCurrencyBankAccountData: any = [];
  financialStatementData: any[] = [];
  intermediaryId: string = INTERMEDIARY_ID;
  currentDate = moment().format("YYYY-MM-DD");

  constructor(
    private investmentsHttpService: InvestmentsHttpService,
    private investmentsFormProvider: InvestmentsFormProvider,
    public activeModal: NgbActiveModal,
    private http: HttpService,
    private config: NgbDatepickerConfig,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
  ) {
    this.initDatePickerConfig();
    this.initializeIntermediaryId();
  }

  ngOnInit(): void {
    this.investorId = this.route.snapshot.queryParamMap.get("id") || "";
    this.investmentForm = this.investmentsFormProvider.initInvestmentForm(this.investmentData);
    if (this.investorId) {
      this.investmentForm.get("investorId")?.setValue(this.investorId);
      this.setPrePopulatedValue();
    }
    this.getDropDownsData();
    this.subscribeToValueChanges();
  }

  initializeIntermediaryId(): void {
    const storedData = localStorage.getItem("userData");
    const userDetails = storedData ? JSON.parse(storedData) : {};
    this.intermediaryId = userDetails.entityRoleId || null;
  }

  onSelect(event: any): void {
    this.isSubmitted = false;
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.investmentForm.invalid) {
      return;
    }
    let rawData = this.investmentForm.value;
    rawData = getFormData(rawData);
    rawData.intermediaryId = this.intermediaryId.toString();
    rawData.investorId = rawData.investorId.toString();
    rawData.months = Number(rawData.months);
    if (rawData.transactionType === "Receipt") {
      rawData.faceValue = Number(rawData.faceValue.replace(/,/g, ""));
    } else {
      rawData.faceValue = null;
    }
    this.doCreate(rawData);
  }

  doCreate(data: any): void {
    this.spinnerService.setShowSpinner(true);
    this.investmentsHttpService.postData("investments", data).subscribe({
      next: (response: any) => {
        this.closeModal();
        this.spinnerService.setShowSpinner(false);
        this.investmentForm.reset();
        Swal.fire({
          icon: "success",
          text: "Investment successfully added.",
          showConfirmButton: false,
          timer: 5000
        });
      },
      error: (error) => {
        this.spinnerService.setShowSpinner(false);
        const errorMessages = error.error.message;
        const containsFutureDateError = Array.isArray(errorMessages)
          ? errorMessages.some((msg: string) => msg.includes("maximal allowed date for"))
          : typeof errorMessages === "string" && errorMessages.includes("maximal allowed date for");

        const defaultMessage = containsFutureDateError ? "Error" : (Array.isArray(errorMessages) ? errorMessages.join(", ") : errorMessages);
        Swal.fire({
          icon: "error",
          text: defaultMessage,
          showConfirmButton: true,
          confirmButtonText: "Ok"
        });
      }
    });
  }

  private getDropDownsData(): void {
    this.investmentsHttpService.getInvestmentsFilterData().subscribe((investmentsFilterData) => {
      investmentsFilterData.types.sort((a, b) => a.localeCompare(b));
      this.type = [
        ...(investmentsFilterData.types.map((type) => ({
          id: type,
          name: type
        })) || [])
      ];
      this.transactionType = [
        ...(investmentsFilterData.transactionTypes
          .filter((transactionType) =>
            !["Redemption", "Adjustment"].includes(transactionType)
          )
          .map((transactionType) => ({
            id: transactionType,
            name: transactionType
          })) || [])
      ];
      this.product = [
        ...(investmentsFilterData.products.map((product) => ({
          id: product,
          name: product
        })) || [])
      ];

      const validTransactionTypes = this.transactionType.map(t => t.id);
      const validTypes = this.type.map(t => t.id);
      const validProducts = this.product.map(t => t.id);

      this.investmentForm.get('transactionType')?.setValidators([Validators.required, this.validOptionValidator(validTransactionTypes)]);
      this.investmentForm.get('type')?.setValidators([Validators.required, this.validOptionValidator(validTypes)]);
      this.investmentForm.get('product')?.setValidators([Validators.required, this.validOptionValidator(validProducts)]);

      ['transactionType', 'type', 'product'].forEach(field => {
        this.investmentForm.get(field)?.updateValueAndValidity();
      });

      /*this.investors = [
        ...(investmentsFilterData.investors
          .sort((a: any, b: any) => {
            const nameA = a?.name.toUpperCase();
            const nameB = b?.name.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          })
          .map((investor: any) => ({
            id: investor.id,
            name: `${investor.id} - ${investor?.name || ""}`
          })) || [])
      ];*/
    });

    this.http.getCurrencyWithBank().subscribe((response: any) => {
      this.allCcyData = [
        ...(response.map((ccy: any) => ({
          id: ccy.currency,
          name: ccy.currency
        })) || [])
      ];

      const validCcyValues = this.allCcyData.map(c => c.id);
      this.investmentForm.get('ccy')?.setValidators([Validators.required, this.validOptionValidator(validCcyValues)]);
      this.investmentForm.get('ccy')?.updateValueAndValidity();
    });

    this.investmentsHttpService.getInvestorsFilterData().subscribe((investor: any) => {
      this.investors = [
        ...(investor.items
          .sort((a: any, b: any) => {
            const nameA = a.entity?.name.toUpperCase();
            const nameB = b.entity?.name.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          })
          .map((investor: any) => {
            const cleanId = investor.id?.split('-')[0];

            return {
              id: cleanId,
              name: `${cleanId} - ${investor?.entity?.name || ''}`,
            };
          }) || []),
      ];
    });
  }

  closeModal() {
    this.activeModal.dismiss();
  }

  private getCurrentDate(): NgbDate {
    const today = new Date();
    return new NgbDate(today.getFullYear(), today.getMonth() + 1, today.getDate());
  }

  initDatePickerConfig() {
    const currentDate = this.getCurrentDate();
    this.config.minDate = { year: 1970, month: 1, day: 1 };
    this.config.maxDate = currentDate;
  }

  subscribeToValueChanges() {
    this.applyFaceValueValidation();
    this.changeSubscription = this.investmentForm.get("months")?.valueChanges.subscribe((monthsValue) => {
      if (!monthsValue) {
        this.investmentForm.get("months")?.setErrors({ required: true });
      } else if (+monthsValue > 0) {
        this.investmentForm.get("months")?.setErrors(null);
      } else {
        this.investmentForm.get("months")?.setErrors({ invalidMonths: true });
      }
    }) as Subscription;

    this.changeSubscription.add(
      this.investmentForm.get("faceValue")?.valueChanges.subscribe((faceValue) => {
        this.applyFaceValueValidation(faceValue);
      }) as Subscription
    );

    this.changeSubscription.add(
      this.investmentForm.get("transactionType")?.valueChanges.subscribe(() => {
        this.applyFaceValueValidation();
      }) as Subscription
    );

  }

  applyFaceValueValidation(faceValue?: any): void {
    const transactionType = this.investmentForm.get("transactionType")?.value;

    if (transactionType !== "Receipt") {
      // If transactionType is not 'Receipt', clear the validation and make the field valid
      this.investmentForm.get("faceValue")?.setErrors(null);
      this.investmentForm.get("faceValue")?.setValidators(null);
      this.investmentForm.get("faceValue")?.markAsPristine();
      this.investmentForm.get("faceValue")?.markAsUntouched();
    } else {
      // Apply the validation checks only if transactionType is 'Receipt'
      this.investmentForm.get("faceValue")?.setValidators([Validators.required, Validators.min(0.01)]);
    }
  }

  setPrePopulatedValue() {
    const data = localStorage.getItem("financialStatementData");
    if (data) {
      this.financialStatementData = JSON.parse(data);
      if (this.financialStatementData && this.financialStatementData.length > 0) {
        this.investmentForm.get("date")?.setValue(this.currentDate);
        const ccy = this.getUniqueValue(this.financialStatementData, "ccy");
        const type = this.getUniqueValue(this.financialStatementData, "type");
        if (type === "Multiple") {
          this.investmentForm.get("type")?.setValue("");
        } else {
          const months = type === "Demand" || type === "Fixed" ? 12 : type === "Term" ? 36 : null;
          if (months !== null) this.investmentForm.get("months")?.setValue(months);
          this.investmentForm.get("type")?.setValue(type);
        }
        this.investmentForm.get("ccy")?.setValue(ccy === "Multiple" ? "" : ccy);
      }
    }
  }

  private validOptionValidator(validValues: string[]): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value || validValues.includes(control.value)) return null;
      return { invalidOption: true };
    };
  }

  getUniqueValue(data: any[], key: string) {
    const uniqueValues = [...new Set(data.map((item) => item[key]))];
    return uniqueValues.length > 1 ? "Multiple" : uniqueValues[0];
  }


  onTypeChange(event: any) {
    const selectedValue = event.target.value;
    const months = selectedValue === "Demand" || selectedValue === "Fixed" ? 12 : selectedValue === "Term" ? 36 : null;
    this.investmentForm.get("months")?.setValue(selectedValue ? months : null);

  }

  handleFocusFaceValue(): void {
    if (isNaN(+this.investmentForm.value.faceValue)) {
      this.investmentForm.get('faceValue')?.patchValue('');
      return;
    }
    this.investmentForm.get('faceValue')?.patchValue(convertISONumberToNormal(+this.investmentForm.value.faceValue));
  }

  handleFocusOutFaceValue(): void {
    if (isNaN(+this.investmentForm.value.faceValue)) {
      this.investmentForm.get('faceValue')?.patchValue('');
      return;
    }
    this.investmentForm.get('faceValue')?.patchValue(formatSimpleNumber(this.investmentForm.value.faceValue));
  }

  ngOnDestroy(): void {
    this.changeSubscription.unsubscribe();
  }
}
